import React, { useEffect, useState, useCallback } from "react";
import { useViewParam } from "@/lib/useViewParam";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/auth/AuthContext";
import LhcHeader from "@/components/LhcHeader";
import { Sk } from "@/components/Skeleton";
import { getTheme as getPlatformTheme, setTheme as setPlatformTheme } from "@/lib/theme";
import { useT } from "../i18n";
import LanguagePicker from "../i18n/LanguagePicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initialsOf = (name) => (name || "?").split(/\s+/).map((w) => w[0] || "").join("").slice(0, 2).toUpperCase();

const ROLE_LABEL = {
  SUPERADMIN: "Superadmin", ADMIN: "Admin", DEALER_OWNER: "Owner", STORE_ADMIN: "Admin",
  SALES: "Sales", DESIGNER: "Designer", CATALOG_MANAGER: "Catalog manager", INSTALLER: "Installer", CLIENT: "Client",
};
// Roles a Dealer Owner / Store Admin may mint — Designer + Installer ONLY
// (2026-07-14 user mandate; matches backend STORE_ADMIN_CREATABLE_ROLES, which
// already rejects anything else server-side).
const CREATABLE_ROLES = [
  ["DESIGNER", "Designer"], ["INSTALLER", "Installer"],
];
const genPassword = () => "Lhc" + Math.random().toString(36).slice(2, 8) + "!";

const NAV = [
  { key: "profile", label: "Profile" },
  { key: "company", label: "Company" },
  { key: "team", label: "Team" },
  { key: "preferences", label: "Preferences" },
  { key: "notifications", label: "Notifications" },
  { key: "security", label: "Security" },
];

export default function AccountPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useViewParam("tab", "profile");
  const b = user?.branding_context || {};
  // Only owners/admins manage the team; sales/designer/catalog/installer don't see it.
  const canManageTeam = ["DEALER_OWNER", "STORE_ADMIN", "SUPERADMIN", "ADMIN"].includes(user?.role);
  const navItems = NAV.filter((n) => n.key !== "team" || canManageTeam);

  useEffect(() => { document.body.setAttribute("data-app-view", "account"); return () => document.body.removeAttribute("data-app-view"); }, []);

  return (
    <>
      <LhcHeader showSearch={false} />
      <section className="account-page" id="account-page" style={{ display: "block" }} data-testid="account-page">
        <div className="account-container">
          <div className="account-head-row">
            <button className="orders-back" onClick={() => nav("/store")}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
              <span>Back</span>
            </button>
          </div>
          <div className="account-title-row">
            <div>
              <h1 className="account-title">Account</h1>
              <p className="account-sub">Manage your profile, company info, and team.</p>
            </div>
          </div>

          <div className="account-body">
            <aside className="account-side">
              <div className="account-identity">
                <div className="account-identity-avatar-wrap">
                  <div className="account-identity-avatar">{initialsOf(user?.name)}</div>
                </div>
                <div className="account-identity-name">{user?.name}</div>
                <div className="account-identity-email">{user?.email}</div>
                <div className="account-identity-role">{ROLE_LABEL[user?.role] || user?.role}</div>
              </div>
              <ul className="account-nav">
                {navItems.map((n) => (
                  <li key={n.key}>
                    <button className={`account-nav-btn ${tab === n.key ? "active" : ""}`} onClick={() => setTab(n.key)} data-testid={`acct-tab-${n.key}`}>
                      <span>{n.label}</span>
                    </button>
                  </li>
                ))}
                <li className="account-nav-sep" />
                <li>
                  <button className="account-nav-btn danger" onClick={() => logout().finally(() => nav("/login"))} data-testid="account-signout">
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
                    <span>Sign out</span>
                  </button>
                </li>
              </ul>
            </aside>

            <div className="account-main">
              {tab === "profile" && <ProfileSection user={user} />}
              {tab === "company" && <CompanySection b={b} />}
              {tab === "team" && canManageTeam && <TeamSection currentUser={user} />}
              {tab === "preferences" && <PreferencesSection />}
              {tab === "notifications" && <NotificationsSection />}
              {tab === "security" && <SecuritySection />}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

const fieldRow = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "0.5px solid var(--chrome-border)" };
const fieldLabel = { fontSize: 12.5, color: "var(--text-tertiary)" };
const fieldValue = { fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)" };

function ProfileSection({ user }) {
  return (
    <div className="account-section active" data-account-tab="profile" style={{ display: "block" }}>
      <div className="nc-card acct-card" style={{ padding: 22 }}>
        <h2 className="nc-card-title" style={{ marginBottom: 6 }}>Personal information</h2>
        <div style={fieldRow}><span style={fieldLabel}>Full name</span><span style={fieldValue}>{user?.name || "—"}</span></div>
        <div style={fieldRow}><span style={fieldLabel}>Email</span><span style={fieldValue}>{user?.email || "—"}</span></div>
        <div style={{ ...fieldRow, borderBottom: "none" }}><span style={fieldLabel}>Role</span><span style={fieldValue}>{ROLE_LABEL[user?.role] || user?.role}</span></div>
      </div>
    </div>
  );
}

function CompanySection({ b }) {
  return (
    <div className="account-section active" data-account-tab="company" style={{ display: "block" }}>
      <div className="nc-card acct-card" style={{ padding: 22 }}>
        <h2 className="nc-card-title" style={{ marginBottom: 6 }}>Company</h2>
        <div style={fieldRow}><span style={fieldLabel}>Dealership</span><span style={fieldValue}>{b.dealer_name || b.company_name || "—"}</span></div>
        <div style={fieldRow}><span style={fieldLabel}>Tenant</span><span style={fieldValue}>{b.tenant_name || "—"}</span></div>
        <div style={{ ...fieldRow, borderBottom: "none" }}><span style={fieldLabel}>Branding scope</span><span style={fieldValue}>{b.allowed_branding_scope || "—"}</span></div>
      </div>
    </div>
  );
}

function Placeholder({ title, text }) {
  return (
    <div className="account-section active" style={{ display: "block" }}>
      <div className="nc-card acct-card" style={{ padding: 22 }}>
        <h2 className="nc-card-title" style={{ marginBottom: 6 }}>{title}</h2>
        <p style={{ fontSize: 13, color: "var(--text-tertiary)" }}>{text}</p>
      </div>
    </div>
  );
}

function PreferencesSection() {
  // ONE theme engine (V5): the same choice drives store, studio and the
  // Catalog Technician; the engine mirrors the legacy key + stamps the DOM.
  const [theme, setThemeState] = useState(getPlatformTheme);
  const apply = (t) => {
    setThemeState(t);
    setPlatformTheme(t);
  };
  const tt = useT();
  return (
    <div className="account-section active" style={{ display: "block" }}>
      <div className="nc-card acct-card" style={{ padding: 22 }}>
        <h2 className="nc-card-title" style={{ marginBottom: 12 }}>{tt("common.appearance")}</h2>
        <div style={fieldRow}>
          <div><div style={fieldValue}>{tt("studio.appearance")}</div><div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>{tt("studio.light")} / {tt("studio.dark")}</div></div>
          <div style={{ display: "flex", gap: 6 }}>
            {["light", "dark"].map((t) => (
              <button key={t} type="button" onClick={() => apply(t)} className="nc-btn nc-btn-secondary" data-testid={`theme-${t}`}
                style={{ textTransform: "capitalize", ...(theme === t ? { borderColor: "var(--accent)", color: "var(--accent-text)" } : {}) }}>{t === "light" ? tt("studio.light") : tt("studio.dark")}</button>
            ))}
          </div>
        </div>
        {/* THE language setting (V5): one choice drives every app instantly */}
        <div style={fieldRow}>
          <div><div style={fieldValue}>{tt("common.language")}</div><div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>English · Shqip · Italiano · Deutsch · Español · Français · العربية</div></div>
          <LanguagePicker compact data-testid="account-language" />
        </div>
      </div>
    </div>
  );
}

function NotificationsSection() {
  const KEYS = [["email_quotes", "Email me when a quote is approved"], ["email_orders", "Email me on order status changes"], ["inapp", "In-app notifications"]];
  const [prefs, setPrefs] = useState(() => { try { return JSON.parse(localStorage.getItem("lhc_notif") || "{}"); } catch { return {}; } });
  const toggle = (k) => { const next = { ...prefs, [k]: !prefs[k] }; setPrefs(next); try { localStorage.setItem("lhc_notif", JSON.stringify(next)); } catch (_) { /* ignore */ } };
  return (
    <div className="account-section active" style={{ display: "block" }}>
      <div className="nc-card acct-card" style={{ padding: 22 }}>
        <h2 className="nc-card-title" style={{ marginBottom: 12 }}>Notifications</h2>
        {KEYS.map(([k, label], i) => (
          <div key={k} style={{ ...fieldRow, borderBottom: i < KEYS.length - 1 ? fieldRow.borderBottom : "none" }}>
            <span style={fieldValue}>{label}</span>
            <button type="button" onClick={() => toggle(k)} aria-pressed={!!prefs[k]} data-testid={`notif-${k}`}
              style={{ width: 42, height: 24, borderRadius: 20, border: "none", cursor: "pointer", position: "relative", background: prefs[k] ? "var(--accent)" : "var(--bg-overlay)", transition: "background .15s", flexShrink: 0 }}>
              <span style={{ position: "absolute", top: 3, left: prefs[k] ? 21 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left .15s" }} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SecuritySection() {
  const [form, setForm] = useState({ current_password: "", new_password: "" });
  const [msg, setMsg] = useState(""); const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const { user: me } = useAuth();
  const imp = !!(me && me.impersonated_by);          // superadmin session → no current password needed
  const submit = async (e) => {
    e.preventDefault(); if ((!imp && !form.current_password) || !form.new_password) return;
    setBusy(true); setMsg(""); setErr("");
    try { await axios.post(`${API}/auth/change-password`, { ...form, current_password: imp ? "-" : form.current_password }); setMsg("Password updated."); setForm({ current_password: "", new_password: "" }); }
    catch (e2) { const d = e2?.response?.data?.detail; setErr(typeof d === "string" ? d : "Could not change password"); }
    finally { setBusy(false); }
  };
  const inp = { width: "100%", boxSizing: "border-box", height: 42, padding: "0 13px", border: "1px solid var(--border-strong)", borderRadius: 9, background: "var(--bg-surface)", color: "var(--text-primary)", fontFamily: "var(--font)", fontSize: 13.5, outline: "none" };
  return (
    <div className="account-section active" style={{ display: "block" }}>
      <div className="nc-card acct-card" style={{ padding: 22 }}>
        <h2 className="nc-card-title" style={{ marginBottom: 4 }}>Change password</h2>
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 380, marginTop: 12 }}>
          {imp ? <div style={{ fontSize: 12.5, color: "var(--text-secondary)" }} data-testid="pw-imp-note">Superadmin session — set a new password directly.</div>
            : <input type="password" autoComplete="current-password" placeholder="Current password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} style={inp} data-testid="cur-pw" />}
          <input type="password" autoComplete="new-password" placeholder="New password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} style={inp} data-testid="new-pw" />
          {err && <div style={{ color: "#dc2626", fontSize: 12.5 }}>{err}</div>}
          {msg && <div style={{ color: "var(--success)", fontSize: 12.5 }}>{msg}</div>}
          <button className="nc-btn nc-btn-primary" disabled={busy} data-testid="change-pw" style={{ alignSelf: "flex-start" }}>{busy ? "Saving…" : "Update password"}</button>
        </form>
      </div>
    </div>
  );
}

/* ───────────────────────── Team (real data via /admin/users) ───────────────────────── */
function TeamSection({ currentUser }) {
  // three-state loading (2026-07-18): null = loading → skeleton rows; the
  // empty team table renders only once the API confirmed it is empty.
  const [members, setMembers] = useState(null);
  const [search, setSearch] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "DESIGNER", password: genPassword() });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/admin/users`).then(({ data }) => setMembers(data || [])).catch(() => setMembers([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/admin/users`, { name: form.name, email: form.email.toLowerCase(), role: form.role, password: form.password });
      setShowInvite(false); setForm({ name: "", email: "", role: "DESIGNER", password: genPassword() });
      load();
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not add member");
    } finally { setBusy(false); }
  };

  const toggleStatus = async (m) => {
    const next = m.status === "suspended" ? "active" : "suspended";
    await axios.patch(`${API}/admin/users/${m.id}`, { status: next }).catch(() => {});
    load();
  };

  const visible = (members || []).filter((m) =>
    !search || (m.name || "").toLowerCase().includes(search.toLowerCase()) || (m.email || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="account-section active" data-account-tab="team" style={{ display: "block" }}>
      <div className="acct-team-page-head">
        <div>
          <h2 className="acct-team-page-title">Team</h2>
          <p className="acct-team-page-sub">Manage people in your dealership who can sign in. Add or remove members.</p>
        </div>
        <button className="nc-btn nc-btn-primary acct-team-invite-btn" onClick={() => { setErr(""); setShowInvite((v) => !v); }} data-testid="team-invite-toggle">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
          <span>Add member</span>
        </button>
      </div>

      <div className="nc-card acct-team-card">
        <div className="acct-team-table-head">
          <div className="acct-team-title-row">
            <h3 className="acct-team-count-title">Team members</h3>
            <span className="acct-team-count-badge">{members == null ? <Sk w={14} h={11} r={4} /> : members.length}</span>
          </div>
          <div className="acct-team-controls">
            <div className="acct-team-search">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.35-4.35" /></svg>
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search members…" />
            </div>
          </div>
        </div>

        {showInvite && (
          <form className="team-invite-form" onSubmit={invite} style={{ display: "block" }} data-testid="team-invite-form">
            <div className="team-invite-grid">
              <div className="nc-field">
                <label>Full name <span className="nc-req">*</span></label>
                <input className="nc-input" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Marco Rossi" data-testid="invite-name" />
              </div>
              <div className="nc-field">
                <label>Work email <span className="nc-req">*</span></label>
                <input className="nc-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="marco@dealer.com" data-testid="invite-email" />
              </div>
              <div className="nc-field">
                <label>Role <span className="nc-req">*</span></label>
                <select className="nc-input nc-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="invite-role">
                  {CREATABLE_ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <div className="nc-field">
                <label>Temporary password <span className="nc-req">*</span></label>
                <input className="nc-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="invite-password" />
              </div>
            </div>
            {err && <div style={{ color: "#dc2626", fontSize: 12.5, marginTop: 8 }} data-testid="invite-error">{err}</div>}
            <div className="team-invite-actions">
              <button type="button" className="nc-btn nc-btn-secondary" onClick={() => setShowInvite(false)}>Cancel</button>
              <button type="submit" className="nc-btn nc-btn-primary" disabled={busy} data-testid="invite-submit">{busy ? "Adding…" : "Add member"}</button>
            </div>
          </form>
        )}

        <div className="acct-team-table-wrap">
          <table className="acct-team-table">
            <thead>
              <tr><th className="col-member">Member</th><th className="col-role">Role</th><th className="col-status">Status</th><th className="col-actions">Actions</th></tr>
            </thead>
            <tbody data-testid="team-tbody">
              {members == null && (
                [0, 1, 2].map((i) => (
                  <tr key={`sk${i}`}>
                    <td className="col-member" colSpan={4} style={{ padding: "10px 14px" }}>
                      <Sk w="100%" h={34} r={8} style={{ display: "block" }} />
                    </td>
                  </tr>
                ))
              )}
              {members != null && visible.map((m) => (
                <tr key={m.id} data-testid={`team-row-${m.id}`}>
                  <td className="col-member">
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--bg-elevated)", border: "0.5px solid var(--chrome-border)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "var(--text-secondary)", flexShrink: 0 }}>{initialsOf(m.name)}</div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>{m.name}</div>
                        <div style={{ fontSize: 11.5, color: "var(--text-tertiary)" }}>{m.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="col-role"><span style={{ fontSize: 12.5 }}>{ROLE_LABEL[m.role] || m.role}</span></td>
                  <td className="col-status">
                    <span style={{ fontSize: 11.5, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: m.status === "suspended" ? "rgba(220,38,38,.10)" : "rgba(22,163,74,.10)", color: m.status === "suspended" ? "#dc2626" : "#16a34a" }}>{m.status === "suspended" ? "Suspended" : "Active"}</span>
                  </td>
                  <td className="col-actions">
                    {m.id === currentUser?.id ? (
                      <span style={{ fontSize: 11.5, color: "var(--text-tertiary)" }}>You</span>
                    ) : (
                      <button className="nc-btn nc-btn-secondary" style={{ padding: "5px 12px", fontSize: 12 }} onClick={() => toggleStatus(m)} data-testid={`team-toggle-${m.id}`}>
                        {m.status === "suspended" ? "Activate" : "Suspend"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {members != null && visible.length === 0 && <tr><td colSpan={4} style={{ padding: "20px 0", fontSize: 13, color: "var(--text-tertiary)" }}>No members found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
